import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSubscriptionStatus } from "../services/stripeService";
import { useAuth } from "./authProvider";
import { getNextPlan, normalizePlan } from "../utils/planTiers";

import logger from "utils/logger";
const StripeContext = createContext();

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error("useStripe must be used within a StripeProvider");
  }
  return context;
};

export const StripeProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();

  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isPremium: false,
    isDiamond: false,
    planId: "free",
    status: "inactive", // active, inactive, canceled, past_due
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
  });

  const [loading, setLoading] = useState(true);

  const setFreePlanState = () => {
    setSubscriptionStatus({
      isPremium: false,
      isDiamond: false,
      planId: "free",
      status: "inactive",
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
    });
  };

  // Fetch subscription status from backend
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setFreePlanState();
      setLoading(false);
      return;
    }

    const fetchSubscriptionStatus = async () => {
      try {
        setLoading(true);

        const response = await getSubscriptionStatus();
        const data = response.data;

        setSubscriptionStatus({
          isPremium: data.planId === "premium" && data.status === "active",
          isDiamond: data.planId === "diamond" && data.status === "active",
          planId: data.planId || "free",
          status: data.status || "inactive",
          currentPeriodEnd: data.currentPeriodEnd,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
        });
      } catch (error) {
        logger.error("Error fetching subscription status:", error);
        // Set default free plan on error
        setFreePlanState();
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [user, authLoading]);

  // Refresh subscription status (call after webhook events)
  //
  // Memoizada: a página de sucesso do pagamento tem esta função nas
  // dependências de um efeito. Recriada a cada render, cada refresh mudava o
  // estado, gerava uma função nova e disparava o efeito de novo — loop de
  // verifySession + getSubscriptionStatus contra o Stripe.
  const refreshSubscriptionStatus = useCallback(async () => {
    if (!user) {
      setFreePlanState();
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getSubscriptionStatus();
      const data = response.data;

      setSubscriptionStatus({
        isPremium: data.planId === "premium" && data.status === "active",
        isDiamond: data.planId === "diamond" && data.status === "active",
        planId: data.planId || "free",
        status: data.status || "inactive",
        currentPeriodEnd: data.currentPeriodEnd,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
      });
    } catch (error) {
      logger.error("Error refreshing subscription status:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Plano efetivo: só conta como pago se a assinatura estiver ativa.
  const currentPlan =
    subscriptionStatus.status === "active"
      ? normalizePlan(subscriptionStatus.planId)
      : "free";
  // Plano a ser oferecido nos CTAs. `null` para Diamante (nada acima).
  //
  // Enquanto `loading`, o status ainda não chegou e TODO usuário parece Free.
  // Devolver "premium" aqui faria um assinante Premium ver "Ativar Premium"
  // piscando a cada carregamento — o bug original, só que intermitente. Sem
  // saber o plano, não há oferta: nenhum CTA renderiza até o status chegar.
  const nextPlan = loading ? null : getNextPlan(currentPlan);

  const value = useMemo(
    () => ({
      subscriptionStatus,
      currentPlan,
      nextPlan,
      loading,
      refreshSubscriptionStatus,
    }),
    [subscriptionStatus, currentPlan, nextPlan, loading, refreshSubscriptionStatus],
  );

  return (
    <StripeContext.Provider value={value}>{children}</StripeContext.Provider>
  );
};
